import { m, useScroll, useTransform, AnimatePresence, AnimateSharedLayout } from 'framer-motion'
import { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
// @mui
import { styled } from '@mui/material/styles'
import { Button, Container, Typography, Stack, Grid } from '@mui/material'
// routes
import { PATH_PAGE } from '../../routes/paths'
// hooks
import Iconify from '../../components/iconify'
import { MotionContainer, varFade } from '../../components/animate'
import { AWS_CDN_DOMAIN } from 'src/config-global'

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(10, 0),
  [theme.breakpoints.up('md')]: {
    height: '80vh', // Desktop 80vh, mobile 100vh
    padding: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: 0,
    right: 0,
    height: '10px',
    backgroundColor: '#fff',
  },
}))

const StyledDescription = styled('div')(({ theme }) => ({
  maxWidth: 1000,
  margin: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'left',
  justifyContent: 'left',
  padding: theme.spacing(15, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(10, 0),
  },
  height: '100%',
  zIndex: 10,
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start', // Adjust alignment for desktop
    justifyContent: 'flex-start', // Adjust alignment for desktop
    marginLeft: -100,
  },
}))

const StyledBackgroundVideo = styled('video')(({ theme }) => ({
  position: 'absolute',
  [theme.breakpoints.up('md')]: {
    top: 167,
  },
  top: 70, // Desktop 165, mobile 70
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: -2,
  objectFit: 'cover', // similar to backgroundSize: 'cover' for images
  // opacity: 2, // Adjust the opacity as needed
  width: '100%',
  height: '100%',

  filter: 'blur(0.3px)', // Adjust the blur intensity as needed
  opacity: 0.9, // Adjust the opacity as needed

  // Make video top fading in white gradient
  '&::before': {
    content: "''",
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.8) 100%)',
  },
}))

const StyledOverlay = styled('div')(({ theme }) => ({
  position: 'absolute',
  [theme.breakpoints.up('md')]: {
    top: 167,
  },
  top: 70, // Desktop 165, mobile 70
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(4, 28, 74, 0.5)',
  zIndex: -1,
}))
// ----------------------------------------------------------------------

export default function HomeHero() {
  const { scrollYProgress } = useScroll()
  const scale = useTransform(scrollYProgress, [0, 1], [1, 2])

  const [hide, setHide] = useState(false)

  useEffect(
    () =>
      scrollYProgress.onChange((scrollHeight) => {
        if (scrollHeight > 0.8) {
          setHide(true)
        } else {
          setHide(false)
        }
      }),
    [scrollYProgress],
  )

  if (hide) {
    return null
  }

  return (
    <StyledRoot>
      <StyledBackgroundVideo autoPlay muted loop src={AWS_CDN_DOMAIN + '/frontend-files/hero-video.webm'} />

      <StyledOverlay />
      <Container component={MotionContainer} sx={{ height: 1 }}>
        <Grid container spacing={10} sx={{ height: 1 }}>
          <Grid item xs={1} md={1} sx={{ height: 1 }}></Grid>
          <Grid item xs={10} md={10} sx={{ height: 1 }}>
            <Description scale={scale} />
          </Grid>
          <Grid item xs={1} md={1} sx={{ height: 1 }}></Grid>
        </Grid>
      </Container>
    </StyledRoot>
  )
}

// ----------------------------------------------------------------------
function Description({ scale }) {
  const text = ['DRIVING SUPPLY CHAIN', 'EXCELLENCE']

  const charPoses = {
    exit: { opacity: 0, y: 20 },
    enter: { opacity: 1, y: 0 },
  }

  return (
    <AnimatePresence>
      <AnimateSharedLayout>
        <StyledDescription
          sx={{
            mt: { xs: 0, md: 10 },
          }}
        >
          {text.map((t, i) => (
            <m.div className="animatedText" variants={varFade().inDown}>
              <Typography
                variant="h5"
                fontSize={{ xs: 50, md: 70 }}
                fontWeight={400}
                sx={{
                  color: i % 2 === 0 ? '#fff' : '#26D970',
                  lineHeight: 1,
                  textAlign: 'left',
                  fontWeight: 700,
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
                }}
                style={{ scale }}
              >
                {[...t].map((char, j) => (
                  <m.span key={j} variants={charPoses}>
                    {char}
                  </m.span>
                ))}
              </Typography>
            </m.div>
          ))}
          {/* Other parts of your code */}
          <m.div variants={varFade().inUp}>
            <Typography
              variant="body2"
              sx={{
                textAlign: 'left',
                lineHeight: 1.3,
                fontWeight: 400,
                fontSize: 20,
                mt: 3,
                color: '#F3F4F6',
              }}
            >
              Cubistics lightens your supply chain load with customized transportation and logistics solutions that deliver on time and on
              budget every time.
            </Typography>
          </m.div>

          <m.div variants={varFade().inUp} sx={{ display: 'flex', justifyContent: 'left' }}>
            <Stack spacing={1.5} direction={{ xs: 'column', sm: 'row' }} sx={{ my: 5 }} alignItems="left" justifyContent="left">
              <Button
                component={RouterLink}
                to={PATH_PAGE.shippers}
                variant="contained"
                startIcon={<Iconify icon="ri:ship-2-line" width={24} />}
                sx={{
                  color: 'black', // Set typography color to black
                  backgroundColor: 'white', // Set background color to white
                  borderRadius: '999px',
                  padding: '12px 32px',
                  '@media (max-width: 600px)': {
                    padding: '9px 18px',
                  },
                  transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.7)', // Dull white on hover
                    transform: 'translateY(-5px)',
                    transition: 'transform 0.3s ease-in-out',
                  },
                }}
              >
                Shippers
              </Button>
              <Button
                component={RouterLink}
                to={PATH_PAGE.carriers}
                variant="contained"
                startIcon={<Iconify icon="octicon:container-16" width={24} />}
                sx={{
                  color: 'black', // Set typography color to black
                  backgroundColor: 'white', // Set background color to white
                  borderRadius: '999px',
                  padding: '12px 32px',
                  '@media (max-width: 600px)': {
                    padding: '9px 18px',
                  },
                  transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.7)', // Dull white on hover
                    transform: 'translateY(-5px)',
                    transition: 'transform 0.3s ease-in-out',
                  },
                }}
              >
                Carriers
              </Button>
            </Stack>
          </m.div>
        </StyledDescription>
      </AnimateSharedLayout>
    </AnimatePresence>
  )
}
